import { useEffect, useState } from 'react';
import { Panel, Flex, Input, Button, CellList, CellSimple, CellHeader, Typography } from '@maxhub/max-ui';
import { useHousesStore } from '../store/houses.store.ts';
import { correctReading } from '../api/houses.ts';
import { formatPeriod, formatDate, isCorrectionWindowOpen } from '../utils/period.ts';

const METER_LABEL: Record<string, string> = {
  cold: '🔵 Холодная вода',
  hot: '🔴 Горячая вода',
};

interface ReadingsHistoryPageProps {
  meterId: string;
  onBack: () => void;
}

export function ReadingsHistoryPage({ meterId, onBack }: ReadingsHistoryPageProps) {
  const { meters, selectedHouseId, readings, loadReadings } = useHousesStore();

  const houseMeters = selectedHouseId ? (meters[selectedHouseId] ?? []) : [];
  const meter = houseMeters.find((m) => m.id === meterId) ?? null;
  const meterReadings = readings[meterId] ?? [];

  const [correctingId, setCorrectingId] = useState<string | null>(null);
  const [correctValue, setCorrectValue] = useState('');
  const [correcting, setCorrecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canCorrect = isCorrectionWindowOpen();

  useEffect(() => {
    loadReadings(meterId);
  }, [meterId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCorrect = async (readingId: string) => {
    const numValue = parseFloat(correctValue.replace(',', '.'));
    if (isNaN(numValue) || numValue < 0) {
      setError('Введите корректное значение');
      return;
    }

    setCorrecting(true);
    setError(null);

    try {
      await correctReading(readingId, numValue);
      setCorrectingId(null);
      setCorrectValue('');
      await loadReadings(meterId);
    } catch (err: unknown) {
      const msg = await extractErrorMessage(err);
      setError(msg);
    } finally {
      setCorrecting(false);
    }
  };

  return (
    <Panel style={{ padding: '1rem', maxWidth: 480, margin: '0 auto' }}>
      <Button mode="link" onClick={onBack} style={{ marginBottom: '1rem' }}>
        ← Назад
      </Button>

      <Typography.Headline variant="medium-strong" style={{ display: 'block', marginBottom: '0.25rem' }}>
        {meter ? (METER_LABEL[meter.type] ?? meter.type) : 'История показаний'}
      </Typography.Headline>

      {meter?.serialNumber && (
        <Typography.Label style={{ display: 'block', opacity: 0.6, marginBottom: '0.75rem' }}>
          № {meter.serialNumber}
        </Typography.Label>
      )}

      {canCorrect && (
        <div
          style={{
            background: '#fff8e1',
            color: '#f57f17',
            borderRadius: 8,
            padding: '0.5rem 0.75rem',
            fontSize: '0.8rem',
            marginBottom: '1rem',
          }}
        >
          ✏️ Окно корректировки открыто (20–25 число)
        </div>
      )}

      {meterReadings.length === 0 ? (
        <Typography.Label style={{ display: 'block', opacity: 0.5, textAlign: 'center', padding: '2rem 0' }}>
          Показаний нет
        </Typography.Label>
      ) : (
        <CellList mode="island">
          <CellHeader>Показания</CellHeader>
          {meterReadings.map((r, idx) => {
            const prev = meterReadings[idx + 1];
            const consumption = prev ? (Number(r.value) - Number(prev.value)).toFixed(2) : null;
            const isEditing = correctingId === r.id;

            return (
              <div key={r.id}>
                <CellSimple
                  title={
                    <Flex align="center" gap={6}>
                      <span style={{ fontWeight: 600 }}>{formatPeriod(r.period)}</span>
                      {r.isCorrected && (
                        <span style={{ fontSize: '0.65rem', background: '#fff3e0', color: '#e65100', borderRadius: 4, padding: '0.1rem 0.3rem' }}>
                          скорр.
                        </span>
                      )}
                    </Flex>
                  }
                  subtitle={formatDate(r.readingDate)}
                  after={
                    <Flex align="baseline" gap={4}>
                      <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>{Number(r.value).toFixed(2)}</span>
                      <span style={{ fontSize: '0.8rem', opacity: 0.6 }}>м³</span>
                      {consumption !== null && (
                        <span style={{ fontSize: '0.75rem', color: '#2e7d32', marginLeft: '0.25rem' }}>
                          +{consumption}
                        </span>
                      )}
                      {canCorrect && !isEditing && (
                        <Button
                          mode="tertiary"
                          size="small"
                          onClick={() => {
                            setCorrectingId(r.id);
                            setCorrectValue(r.value);
                            setError(null);
                          }}
                          style={{ marginLeft: '0.25rem' }}
                        >
                          ✏️
                        </Button>
                      )}
                    </Flex>
                  }
                />

                {isEditing && (
                  <Flex gap={8} style={{ padding: '0.5rem 1rem 0.75rem', flexWrap: 'wrap' }}>
                    <Input
                      mode="primary"
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      value={correctValue}
                      onChange={(e) => { setCorrectValue(e.target.value); setError(null); }}
                      disabled={correcting}
                      autoFocus
                      style={{ flex: 1, textAlign: 'right' }}
                    />
                    <Button
                      mode="primary"
                      size="small"
                      loading={correcting}
                      disabled={correcting}
                      onClick={() => handleCorrect(r.id)}
                    >
                      ✓
                    </Button>
                    <Button
                      mode="secondary"
                      size="small"
                      onClick={() => { setCorrectingId(null); setError(null); }}
                    >
                      ✕
                    </Button>
                    {error && (
                      <Typography.Label style={{ display: 'block', width: '100%', color: '#c62828' }}>
                        {error}
                      </Typography.Label>
                    )}
                  </Flex>
                )}
              </div>
            );
          })}
        </CellList>
      )}
    </Panel>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function extractErrorMessage(err: unknown): Promise<string> {
  try {
    if (err && typeof err === 'object' && 'response' in err) {
      const res = (err as { response: Response }).response;
      const body = await res.json();
      return body?.error ?? 'Ошибка при корректировке';
    }
  } catch {
    // ignore
  }
  return 'Ошибка при корректировке';
}
